import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { getDistributors, resolveMedia } from "@/lib/api";

function normalizeWebsiteUrl(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
    return cleanUrl;
  }

  return `https://${cleanUrl}`;
}

export default function DistributorsPage() {
  const [distributors, setDistributors] = useState([]);

  useEffect(() => {
    getDistributors()
      .then((r) => {
        const sortedDistributors = [...(r.data || [])].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "es", {
            sensitivity: "base",
          })
        );

        setDistributors(sortedDistributors);
      })
      .catch(() => {});
  }, []);

  return (
    <main
      data-testid="distributors-page"
      className="pt-20 lg:pt-24 bg-construtop-texture"
    >
      <section className="bg-emerald-950 py-14 lg:py-16 section-padding">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-2xl sm:text-3xl text-white tracking-[0.24em]"
          >
            DISTRIBUIDORES
          </motion.h1>

          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
            Home / Distribuidores
          </span>
        </div>
      </section>

      <section className="py-16 lg:py-20 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-20 gap-y-14 text-center">
          {distributors.map((dist, i) => {
            const websiteUrl = normalizeWebsiteUrl(dist.website_url);
            const hasWebsite = Boolean(websiteUrl);

            const logoContent = dist.logo_url ? (
              <img
                src={resolveMedia(dist.logo_url)}
                alt={dist.name}
                className={`max-h-20 max-w-36 object-contain transition-all duration-300 ${
                  hasWebsite ? "group-hover:scale-105" : ""
                }`}
              />
            ) : (
              <div className="text-3xl font-heading text-emerald-700">
                {dist.name
                  ?.split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 3)}
              </div>
            );

            return (
              <motion.article
                key={dist.id || i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                data-testid={`distributor-card-${i}`}
                className="min-h-[160px] flex flex-col items-center justify-start"
              >
                <h3 className="text-[13px] text-zinc-700 mb-4 font-medium leading-tight min-h-[18px]">
                  {dist.name}
                </h3>

                <div className="h-20 w-36 flex items-center justify-center mb-4">
                  {hasWebsite ? (
                    <a
                      href={websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Abrir página de ${dist.name}`}
                      title={`Abrir página de ${dist.name}`}
                      data-testid={`distributor-link-${i}`}
                      className="group h-20 w-36 flex items-center justify-center cursor-pointer"
                    >
                      {logoContent}
                    </a>
                  ) : (
                    logoContent
                  )}
                </div>

                <p className="text-[12px] text-zinc-800 leading-tight">
                  <strong>Teléfono:</strong> {dist.phone}
                </p>

                <p className="text-[12px] text-zinc-500 leading-tight">
                  Ciudad: {dist.city}
                </p>

                {hasWebsite && (
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900 transition-colors"
                  >
                    Visitar sitio
                    <ExternalLink size={11} />
                  </a>
                )}
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="bg-zinc-100 py-12 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-lg text-zinc-700 mb-2">
              ÚNETE A NUESTRO MAIL INFORMATIVO
            </h2>

            <p className="text-xs text-zinc-500 max-w-sm">
              Y recibe directo en tu correo las novedades de productos y ofertas
              que tenemos para ti
            </p>
          </div>

          <form
            className="flex flex-col items-start gap-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="email"
              placeholder="Ingresa tu email"
              className="w-full md:w-64 h-8 border border-zinc-400 bg-white px-3 text-xs italic"
            />

            <button className="bg-zinc-700 hover:bg-zinc-800 text-white text-[10px] uppercase tracking-wider px-8 py-3">
              Suscribirse
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}